import React from 'react';
import { Clock, Globe, History, MessageCircle, MoreHorizontal, User } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import VisitorIdentitySummary from '../identity/VisitorIdentitySummary';
import {
  getIdentityVerificationTitle,
  resolveIdentityStatus,
} from '../../lib/visitorIdentity';
import { deriveVisitorStatus, type VisitorPresence } from './liveViewModel';

interface LiveVisitorCardProps {
  visitor: VisitorPresence;
  accent: 'blue' | 'orange' | 'green';
  onOpenDetails: () => void;
  onOpenPrimary: () => void;
  onViewAllSessions: () => void;
}

const statusLabels = {
  onsite: 'Onsite',
  in_app: 'In-app',
  web_session: 'Web session',
  app_session: 'App session',
  offline: 'Offline',
};

function pageLabel(url?: string | null): string {
  if (!url) return 'No current page';
  try {
    const parsed = new URL(url);
    return parsed.pathname === '/' ? 'Homepage' : parsed.pathname;
  } catch {
    return url;
  }
}

const LiveVisitorCard: React.FC<LiveVisitorCardProps> = ({
  visitor,
  accent,
  onOpenDetails,
  onOpenPrimary,
  onViewAllSessions,
}) => {
  const status = deriveVisitorStatus(visitor);
  const identityStatus = resolveIdentityStatus({
    summaryStatus: visitor.identity?.summary?.status,
    lead: visitor.identity?.lead,
    person: visitor.identity?.person,
    company: visitor.identity?.company,
  });
  // Offline check first: session_active lingers ~16 min after a visitor
  // leaves, and only truly present visitors can be taken over.
  const isPresent = Boolean(visitor.online);
  const primaryLabel = !isPresent && visitor.latest_conversation_id
    ? 'View last session'
    : !isPresent
      ? 'Offline'
      : visitor.session_active || visitor.latest_conversation_id
        ? 'Take over chat'
        : 'Start chat';
  const primaryDisabled = !isPresent && !visitor.latest_conversation_id;
  const accentClass = {
    blue: 'border-l-blue-500',
    orange: 'border-l-orange-500',
    green: 'border-l-emerald-500',
  }[accent];

  return (
    <Card
      role="button"
      tabIndex={0}
      className={`border-l-4 ${accentClass} cursor-pointer transition-colors hover:bg-muted/30`}
      onClick={onOpenDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onOpenDetails();
      }}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <VisitorIdentitySummary
              status={identityStatus}
              lead={visitor.identity?.lead}
              person={visitor.identity?.person}
              company={visitor.identity?.company}
              subtitle={visitor.identity?.summary?.subtitle}
              verifiedTitle={getIdentityVerificationTitle({
                leadIdentityVerifiedAt: visitor.identity?.lead?.identity_verified_at,
                sessionIdentityVerifiedAt: visitor.identity?.visitor_profile?.identity_verified_at,
              })}
            />
          </div>
          <Badge variant="secondary" className="text-[10px] whitespace-nowrap">
            {statusLabels[status]}
          </Badge>
        </div>

        <div className="space-y-1.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{pageLabel(visitor.current_page)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>{visitor.last_seen ? new Date(visitor.last_seen).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'No activity'}</span>
            {visitor.conversation_count ? (
              <button
                type="button"
                className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted hover:text-foreground"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewAllSessions();
                }}
                aria-label={`View ${visitor.conversation_count} sessions`}
                title="View all sessions"
              >
                <History className="h-3 w-3" />
                {visitor.conversation_count}
              </button>
            ) : null}
          </div>
          {visitor.human_agent && (
            <div className="truncate">Rep: {visitor.human_agent.name || visitor.human_agent.email}</div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            className="h-8 flex-1 text-xs"
            disabled={primaryDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onOpenPrimary();
            }}
          >
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
            {primaryLabel}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                onClick={(event) => event.stopPropagation()}
                aria-label="Visitor actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
              <DropdownMenuItem onClick={onOpenDetails}>View details</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveVisitorCard;
